import { ComponentInternalInstance, getCurrentInstance, Ref } from '@vue/composition-api';
import i18n, { i18n_t } from '@/locales/i18n';
import smh, { cancelRequest, SMH } from '@/renderer/scripts/smh';
import ipc from '@/renderer/scripts/utils/ipc';
import tips, { MsgError } from '@/renderer/scripts/utils/tips';
import spaceATM from '@/renderer/scripts/utils/AccessTokenManager/space';
import store from '@/renderer/store';
import { checkToNumber } from '@/renderer/scripts/utils/tools';
type Handler = {
  methods: {
    addFavorite: (
      item: {
        path: string[];
        id?: number;
        spaceOrgId?: number;
      },
      list: any,
    ) => void;
    delFavorite: (item: number[], list?: any, spaceOrgId?: number, purpose?: string) => void;
    getFileExtraRefresh: (
      tableData: { list: any[] },
      option: {
        multiSpaceId?: boolean;
        targetList?: any[];
        noSensitiveWordTag?: boolean;
      },
    ) => void;
    getFileExtra: (list: any[], multiSpaceId?: boolean) => Promise<SMH.GetFileExtraInfoData[]>;
  };
};
export default function extraFileInfoHandler(spaceId?: Ref<SpaceId>): Handler {
  // 当前组件
  const currComponent: ComponentInternalInstance = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;
  const methods = {
    async addFavorite(
      item: { path: string[]; id?: number; spaceId?: string; spaceOrgId?: number },
      list: SMH.FileItemBaseData[],
    ) {
      try {
        const params = {
          spaceId: spaceId?.value || (await spaceATM.getPersonalSpaceIdAsync()),
          path: item.path?.join('/') as string,
          spaceOrgId: item?.spaceOrgId || vm.$store.getters.orgId,
        };
        const res = await smh.userAPI.addFavorite(params);
        list.forEach((i: any) => {
          /**
           * 1、第一种情况，i.id === item.id（第一条数据id可能为0，需要通过typeof去判断）
           * 2、第二种情况，空间相同 & path相同（如与我相关中）
           */
          if (
            (typeof i.id === 'number' && typeof item.id === 'number' && i.id === item.id) ||
            (i.path.join('/') === item.path?.join('/') && i.spaceId && item.spaceId && i.spaceId === item.spaceId)
          ) {
            i.favoriteId = res.data.id;
            i.favorited = true;
          }
        });
        tips.success(i18n_t('收藏成功'));
      } catch (error) {
        tips.error(error as MsgError);
      }
    },
    async delFavorite(item: number[], list?: any, spaceOrgId?: number, purpose?: string) {
      try {
        const res = await smh.userAPI.delFavorite({
          favoriteIds: item.join(','),
          spaceOrgId,
        });
        list &&
          list.forEach((i: any) => {
            if (i.favoriteId === item[0]) {
              i.favoriteId = null;
              i.favorited = false;
            }
          });
        ipc.emit(ipc.RENDERER_EVENTS_MAP.FAVOR_LIST_FRESH);
        // 批量清除失效文件
        if (purpose === 'allFailure') {
          tips.success(i18n_t('失效的收藏文件清除成功'));
        } else {
          tips.success(i18n_t('取消收藏成功'));
        }
      } catch (error) {
        tips.error(error as MsgError);
      }
    },
    /**
     * @description 获取文件扩展信息
     * 背景：favorite 是因为最开始文件扩展信息只有一个字段，叫做收藏字段。
     * @param tableData 列表，默认取 tableData.list 请求额外信息，分页使用 targetList
     * @param option multiSpaceId 有一些文件视图，存在多个spaceId,所以
     *  targetList 分页使用该list 请求额外信息
     *  noSensitiveWordTag 是否显示敏感文件 tag （如果当前的群组是外部群组，且不是自己的企业，就不要显示）
     * 1. 不能够在调用extraFileInfoHandler的时候传递spaceId
     * 2. 不能够调用getFileExtraInfo的时候，选择一个spaceId
     * 需要使用item循环来进行逐个取值spaceId
     */
    async getFileExtraRefresh(
      tableData: { list: any[] },
      option: {
        multiSpaceId?: boolean;
        targetList?: any[];
        noSensitiveWordTag?: boolean;
      },
    ) {
      try {
        const res = await methods.getFileExtra(option.targetList ?? tableData?.list, option.multiSpaceId);
        tableData.list = tableData.list.map(item => {
          const index = res.findIndex((value: any) => {
            return value.path === item.path.join('/') && value.spaceId === value.spaceId;
          });
          if (index !== -1) {
            return {
              ...item,
              favoriteId: res[index]?.favoriteId,
              favorited: Boolean(res[index]?.favoriteId),
              isAuthorized: res[index]?.isAuthorized,
              virusAuditStatus: res[index]?.virusAuditStatus,
              sensitiveWordAuditStatus: option.noSensitiveWordTag ? undefined : res[index]?.sensitiveWordAuditStatus,
              canApply: res[index]?.canApply,
              hasApplied: res[index]?.hasApplied,
            };
          }
          return item;
        });
      } catch (err) {
        tips.error(err as MsgError);
      }
    },
    async getFileExtra(list: any = [], multiSpaceId?: boolean): Promise<SMH.GetFileExtraInfoData[]> {
      try {
        const { userBaseInfo, hasPersonalSpace } = store.getters;
        const { isTemporary } = userBaseInfo;
        const tmp = [];
        let SpaceId = '';
        // 如果是没有个人空间，且不是临时成员，可调用获取个人空间接口
        const noSpace = isTemporary || !hasPersonalSpace;
        if (!multiSpaceId && !noSpace) {
          SpaceId = spaceId?.value || (await spaceATM.getPersonalSpaceIdAsync());
        }

        // 如果是临时成员&&spaceId存在，则使用spaceId
        if (noSpace && spaceId?.value) {
          SpaceId = spaceId.value;
        }

        // 若是协作群组中列表的扩展字段，则使用路由的 orgId
        // 否则继续使用原来的orgId
        const routerSpaceOrgId = checkToNumber(vm.$route.query?.spaceOrgId);
        for (let index = 0; index < list.length; index++) {
          const item = list[index];
          const spaceOrgId = (item.spaceOrgId || routerSpaceOrgId) ?? undefined;
          const params = {
            // 此处由于与我相关的收藏为跨空间，所以需要采用原spaceId
            spaceId: item.spaceId ? item.spaceId : SpaceId,
            path: item.path?.join('/') as string,
            ...(spaceOrgId && {
              spaceOrgId,
            }),
          };
          tmp.push(params);
        }
        const res = await smh.userAPI.getFileExtraInfo(
          {
            cancelToken: cancelRequest.token,
          },
          {
            body: tmp,
          },
        );
        return res.data;
      } catch (error: any) {
        // 如果是存储空间不足的就不执行
        if (error.code !== 'NoPersonalSpace') {
          tips.error(error as MsgError);
        }
        return [];
      }
    },
  };
  return {
    methods,
  };
}
